import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import type { PropsWithChildren } from 'react';
import styled from 'styled-components';

import { BrandS, BrandXXS } from '@sorare/core/src/atoms/typography';
import { bezeledBadgeStyle } from '@sorare/core/src/atoms/ui/bezels/bezeledBadge';

import type { CardsCountBadge_userCardCollectionSlot } from './__generated__/index.graphql.ts';

const Wrapper = styled.div`
  position: relative;
`;
const Badge = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  top: -2px;
  right: -2px;
  width: min(32px, 40%);
  height: min(32px, 40%);
  ${bezeledBadgeStyle}
  background: linear-gradient(124deg, #515151 33.02%, #141414 109.93%);
  filter: drop-shadow(0 0 4px #000);
  padding: 0 0 2px 1px;
  &.small {
    top: -1px;
    right: -1px;
    width: min(16px, 40%);
    height: min(16px, 40%);
    filter: drop-shadow(0 0 1px #000);
  }
`;

type Props = {
  size: 'small' | 'medium';
  userCardCollectionSlot: Nullable<CardsCountBadge_userCardCollectionSlot>;
};

export const CardsCountBadge = ({
  size,
  userCardCollectionSlot,
  children,
}: PropsWithChildren<Props>) => {
  const { cardCollectionCardsCount } = userCardCollectionSlot || {};

  const hasMultipleCards = (cardCollectionCardsCount ?? 0) >= 2;
  if (!hasMultipleCards) {
    return <>{children}</>;
  }

  const Text = size === 'small' ? BrandXXS : BrandS;

  return (
    <Wrapper>
      {children}
      <Badge className={size}>
        <Text>{cardCollectionCardsCount}</Text>
      </Badge>
    </Wrapper>
  );
};

CardsCountBadge.fragments = {
  userCardCollectionSlot: gql`
    fragment CardsCountBadge_userCardCollectionSlot on UserCardCollectionSlot {
      slug
      cardCollectionCardsCount
    }
  ` as TypedDocumentNode<CardsCountBadge_userCardCollectionSlot>,
};
